import { useEffect, useRef } from "react";
import AnalyticsService from "services/AnalyticsService";
import useSubscription from "hooks/useSubscription";
import type { UpsellFeature } from "types/upsell";

/**
 * Legacy orgs predate the subscription model and have no `subscriptionDetails`,
 * so they are reported as a named tier rather than `undefined` — otherwise the
 * exact cohort the scope doc calls out would drop out of per-tier analysis.
 */
const LEGACY_PLAN_TYPE = "Legacy";

/**
 * Reports how long the coach spent on an upsell landing page, logged once when
 * they leave.
 */
function useUpsellPageTimeSpent(feature: UpsellFeature) {
  const subscription = useSubscription();
  const planType = subscription?.planType ?? LEGACY_PLAN_TYPE;

  /**
   * The timer effect must run exactly once per visit, so the values it reports
   * are read from refs rather than being effect dependencies. Synced in their
   * own effect — assigning during render is an impure render and misbehaves
   * under StrictMode's double invocation.
   */
  const featureRef = useRef(feature);
  const planTypeRef = useRef(planType);

  useEffect(() => {
    featureRef.current = feature;
    planTypeRef.current = planType;
  }, [feature, planType]);

  useEffect(() => {
    const enteredAt = performance.now();

    return () => {
      AnalyticsService.logEvent(AnalyticsService.EVENTS.UPSELL_PAGE_TIME_SPENT, {
        [AnalyticsService.PROPERTIES.UPSELL_FEATURE]: featureRef.current,
        [AnalyticsService.PROPERTIES.UPSELL_PLAN_TYPE]: planTypeRef.current,
        [AnalyticsService.PROPERTIES.TIME_SPENT_SECONDS]: Math.round((performance.now() - enteredAt) / 1000),
      });
    };
  }, []);
}

export default useUpsellPageTimeSpent;
